// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import { NavbarActionProvider } from 'contexts/NavbarActionContext';
import { AnalysesProvider } from 'contexts/AnalysesContext';
import React, { useState, useEffect, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routesMedecin from 'routesMedecin.js';
import LoadingSpinner from 'components/LoadingSpinner';

// Custom Chakra theme
export default function MedecinLayout(props) {
    const { ...rest } = props;
    // states and functions
    const [fixed] = useState(false);
    const [toggleSidebar, setToggleSidebar] = useState(false);
    const [currentBrandText, setCurrentBrandText] = useState('');
    const location = useLocation();

    // Helper function
    const isWindowAvailable = () => typeof window !== 'undefined';

    useEffect(() => {
        if (isWindowAvailable()) {
            window.document.documentElement.dir = 'ltr';
        }
    });

    // Get active route name
    const getActiveRoute = (currentRoutes, pathname) => {
        let activeRoute = 'Tableau de bord';
        for (let i = 0; i < currentRoutes.length; i++) {
            if (currentRoutes[i].hidden) {
                continue;
            }
            if (currentRoutes[i].collapse) {
                let collapseActiveRoute = getActiveRoute(currentRoutes[i].items, pathname);
                if (collapseActiveRoute !== activeRoute) {
                    return collapseActiveRoute;
                }
            } else if (currentRoutes[i].category) {
                let categoryActiveRoute = getActiveRoute(currentRoutes[i].items, pathname);
                if (categoryActiveRoute !== activeRoute) {
                    return categoryActiveRoute;
                }
            } else {
                if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
                    return currentRoutes[i].name;
                }
            }
        }
        return activeRoute;
    };

    useEffect(() => {
        const getBrandText = (pathname) => {
            for (let i = 0; i < routesMedecin.length; i++) {
                if (routesMedecin[i].hidden) {
                    continue;
                }
                if (routesMedecin[i].category && routesMedecin[i].items) {
                    for (let j = 0; j < routesMedecin[i].items.length; j++) {
                        if (routesMedecin[i].items[j].layout + routesMedecin[i].items[j].path === pathname) {
                            return routesMedecin[i].items[j].name;
                        }
                    }
                }
                if (routesMedecin[i].layout + routesMedecin[i].path === pathname) {
                    return routesMedecin[i].name;
                }
            }
            const activeRouteName = getActiveRoute(routesMedecin, pathname);
            return activeRouteName;
        };
        setCurrentBrandText(getBrandText(location.pathname));
    }, [location.pathname]);

    // functions for changing the states from components
    const getRoute = () => {
        return window.location.pathname !== '/medecin/full-screen-maps';
    };

    const getActiveNavbar = (currentRoutes, pathname) => {
        let activeNavbar = false;
        for (let i = 0; i < currentRoutes.length; i++) {
            if (currentRoutes[i].hidden) {
                continue;
            }
            if (currentRoutes[i].collapse) {
                let collapseActiveNavbar = getActiveNavbar(currentRoutes[i].items, pathname);
                if (collapseActiveNavbar !== activeNavbar) {
                    return collapseActiveNavbar;
                }
            } else if (currentRoutes[i].category) {
                let categoryActiveNavbar = getActiveNavbar(currentRoutes[i].items, pathname);
                if (categoryActiveNavbar !== activeNavbar) {
                    return categoryActiveNavbar;
                }
            } else {
                if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
                    return currentRoutes[i].secondary;
                }
            }
        }
        return activeNavbar;
    };

    const getActiveNavbarText = (currentRoutes, pathname) => {
        let activeNavbarText = false;
        for (let i = 0; i < currentRoutes.length; i++) {
            if (currentRoutes[i].hidden) {
                continue;
            }
            if (currentRoutes[i].collapse) {
                let collapseActiveNavbarText = getActiveNavbarText(currentRoutes[i].items, pathname);
                if (collapseActiveNavbarText !== activeNavbarText) {
                    return collapseActiveNavbarText;
                }
            } else if (currentRoutes[i].category) {
                let categoryActiveNavbarText = getActiveNavbarText(currentRoutes[i].items, pathname);
                if (categoryActiveNavbarText !== activeNavbarText) {
                    return categoryActiveNavbarText;
                }
            } else {
                if (pathname.includes(currentRoutes[i].layout + currentRoutes[i].path)) {
                    return currentRoutes[i].messageNavbar;
                }
            }
        }
        return activeNavbarText;
    };

    const getRoutes = (routes) => {
        return routes.map((route, key) => {
            if (route.layout === '/medecin') {
                return (
                    <Route
                        path={`${route.path}`}
                        element={
                            <Suspense fallback={<LoadingSpinner />}>
                                {route.component}
                            </Suspense>
                        }
                        key={key}
                    />
                );
            }
            // Gérer les catégories
            if (route.category && route.items) {
                return getRoutes(route.items);
            }
            if (route.collapse && route.items) {
                return getRoutes(route.items);
            }
            return null;
        });
    };

    document.documentElement.dir = 'ltr';
    const { onOpen } = useDisclosure();

    return (
        <NavbarActionProvider>
            <AnalysesProvider>
                <Box>
                    <Box>
                        <SidebarContext.Provider
                            value={{
                                toggleSidebar,
                                setToggleSidebar,
                            }}
                        >
                            <Sidebar routes={routesMedecin} display="none" {...rest} />
                            <Box
                                float="right"
                                minHeight="100vh"
                                height="100%"
                                overflow="auto"
                                position="relative"
                                maxHeight="100%"
                                w={{ base: '100%', xl: 'calc( 100% - 240px )' }}
                                maxWidth={{ base: '100%', xl: 'calc( 100% - 240px )' }}
                                transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
                                transitionDuration=".2s, .2s, .35s"
                                transitionProperty="top, bottom, width"
                                transitionTimingFunction="linear, linear, ease"
                            >
                                <Portal>
                                    <Box>
                                        <Navbar
                                            onOpen={onOpen}
                                            logoText={'SAMU 01 - Médecin'}
                                            brandText={currentBrandText}
                                            secondary={getActiveNavbar(routesMedecin, location.pathname)}
                                            message={getActiveNavbarText(routesMedecin, location.pathname)}
                                            fixed={fixed}
                                            {...rest}
                                        />
                                    </Box>
                                </Portal>

                                {getRoute() ? (
                                    <Box
                                        mx="auto"
                                        p={{ base: '20px', md: '30px' }}
                                        pe="20px"
                                        minH="100vh"
                                        pt="50px"
                                    >
                                        <Routes>
                                            {getRoutes(routesMedecin)}
                                            <Route
                                                path="/"
                                                element={<Navigate to="/medecin/default" replace />}
                                            />
                                        </Routes>
                                    </Box>
                                ) : null}
                                <Box>
                                    <Footer />
                                </Box>
                            </Box>
                        </SidebarContext.Provider>
                    </Box>
                </Box>
            </AnalysesProvider>
        </NavbarActionProvider>
    );
}
